import { useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { MessageSquare } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreThread } from '@/client/types/stores/parity';
import { useStoreThreadQuery, useStoreThreadsQuery } from '@/client/api/stores/parity.query';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { cn } from '@/lib/utils';

import { Pager, SectionCard, ListSkeleton } from './shared';

const LIMIT = 25;

const when = (ts: number | null) => (ts ? new Date(ts * 1000).toLocaleString() : '—');

/** `storeuser:{storeid}:{uid}[:eid:{n}]` → the buyer uid and the listing, if any. */
const parseKey = (key: string) => {
  const [, , uid, , eid] = key.split(':');
  return { uid: Number(uid) || null, eid: eid ? Number(eid) : null };
};

const buyerName = (t: StoreThread) => {
  const { uid } = parseKey(t.conversation_key);
  const s = uid ? t.user_summaries?.[String(uid)] : undefined;
  return s?.name || (uid ? `User #${uid}` : 'Buyer');
};

const Thread: React.FC<{ storeid: number; conversationKey: string }> = ({ storeid, conversationKey }) => {
  const { data, isLoading } = useStoreThreadQuery(storeid, conversationKey);
  if (isLoading || !data)
    return (
      <div className="flex flex-col gap-2" aria-busy="true" aria-label="Loading">
        {[60, 45, 70, 40, 55].map((w, i) => (
          <Skeleton key={i} className={cn('h-12 rounded-2xl', i % 2 ? 'self-end' : 'self-start')} style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  return (
    <div className="flex max-h-[520px] flex-col gap-2 overflow-y-auto">
      {data.messages.length === 0 && <p className="py-6 text-center text-sm text-gray-400">No messages.</p>}
      {data.messages.map((m) => {
        const fromStore = m.sender_kind === 'org' || (m as { sender_storeid?: number }).sender_storeid != null;
        return (
          <div key={m.id} className={cn('flex', fromStore ? 'justify-end' : 'justify-start')}>
            <div className={cn('max-w-[75%] rounded-lg px-3 py-2 text-sm', fromStore ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-900', m.is_deleted && 'opacity-50')}>
              <p className="whitespace-pre-wrap break-words">{m.redacted ? '[redacted]' : m.message || '(attachment)'}</p>
              <p className={cn('mt-1 text-[10px]', fromStore ? 'text-teal-100' : 'text-gray-400')}>
                {m.sender_kind === 'platform' ? 'FeelyFeely · ' : ''}{when(m.timestamp)}{m.is_deleted ? ' · deleted' : ''}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * The store's inbox, read-only (`admin.store.messages`): buyer ⇄ store
 * threads from the messaging service. Staff can read but not reply as the store.
 */
export const MessagesTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const allowed = useGate(PERMISSIONS.store.messages).allowed;
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading } = useStoreThreadsQuery(store.storeid, { limit: LIMIT, offset }, allowed);

  if (!allowed) {
    return (
      <SectionCard>
        <p className="text-sm text-gray-500">Reading a store's messages needs the “Read store messages” permission.</p>
      </SectionCard>
    );
  }

  const threads = data?.conversations ?? [];
  return (
    <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
      <SectionCard title="Threads" description="Read-only">
        {isLoading ? (
          <ListSkeleton rows={6} trailing={false} />
        ) : threads.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-400">No conversations.</p>
        ) : (
          <div className="-mx-2 divide-y divide-gray-100">
            {threads.map((t) => {
              const { eid } = parseKey(t.conversation_key);
              return (
                <button
                  key={t.conversation_key}
                  type="button"
                  onClick={() => setOpen(t.conversation_key)}
                  className={cn('flex w-full items-start gap-2 px-2 py-2.5 text-left hover:bg-gray-50', open === t.conversation_key && 'bg-teal-50')}
                >
                  <MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900">{buyerName(t)}</span>
                    <span className="block truncate text-xs text-gray-500">
                      {eid ? `About listing #${eid} · ` : ''}{when(t.last_message_ts)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <Pager total={data?.pagination.total ?? 0} limit={LIMIT} offset={offset} onChange={setOffset} />
      </SectionCard>

      <SectionCard title={open ? 'Conversation' : 'Select a thread'}>
        {open ? <Thread storeid={store.storeid} conversationKey={open} /> : <p className="text-sm text-gray-400">Pick a conversation to read it.</p>}
      </SectionCard>
    </div>
  );
};
